import { useEffect, useRef } from 'react'
import type { RefObject } from 'react'
import { useLocation } from 'react-router-dom'

const KEY_PREFIX = 'crm_scroll_'
// Сколько кадров пытаемся восстановить позицию: AG Grid и асинхронные данные
// (тяжёлые заявки на 900+ позиций) рендерятся не сразу, поэтому ждём до ~4с,
// пока контента не хватит для прокрутки к сохранённой позиции.
const MAX_TRIES = 240

/**
 * Универсальное восстановление позиции скролла при навигации «список → деталь
 * → назад». Сохраняет scrollTop элемента в sessionStorage по ключу и
 * восстанавливает его при следующем монтировании этого же списка.
 *
 * Подходит и для главного контейнера (`<main>`), и для внутреннего вьюпорта
 * AG Grid (`.ag-body-viewport`) — элемент возвращает `getScrollEl`.
 *
 * @param key         уникальный ключ списка (обычно pathname [+ суффикс грида])
 * @param getScrollEl функция, возвращающая скролл-элемент (может вернуть null,
 *                    пока он ещё не отрендерен — тогда ретраим)
 * @param ready       если данные грузятся асинхронно — передать true, когда
 *                    список готов к восстановлению (по умолчанию true)
 */
export function useScrollRestoration(
  key: string,
  getScrollEl: () => HTMLElement | null,
  ready = true,
) {
  const storageKey = KEY_PREFIX + key
  // getScrollEl может пересоздаваться каждый рендер — держим в ref, чтобы
  // эффект не перезапускался и не сбрасывал процесс восстановления.
  const getElRef = useRef(getScrollEl)
  getElRef.current = getScrollEl

  useEffect(() => {
    if (!ready) return
    const saved = Number(sessionStorage.getItem(storageKey) ?? '0')
    let el: HTMLElement | null = null
    let detach: (() => void) | null = null
    let raf = 0
    let tries = 0
    let restored = false

    const onScroll = () => {
      if (el) sessionStorage.setItem(storageKey, String(el.scrollTop))
    }

    const tick = () => {
      const next = getElRef.current()
      if (next) {
        if (next !== el) {
          detach?.()
          el = next
          el.addEventListener('scroll', onScroll, { passive: true })
          detach = () => el?.removeEventListener('scroll', onScroll)
        }
        if (!restored && saved > 0) {
          // Восстанавливаем, только когда контента уже хватает, чтобы
          // доскроллить к сохранённой позиции (данные загружены и строки
          // отрендерены). Иначе ждём следующий кадр — иначе на тяжёлой
          // таблице scrollTop «упрётся» в неполную высоту и слетит в 0.
          const maxScroll = el.scrollHeight - el.clientHeight
          if (maxScroll >= saved - 4 || tries >= MAX_TRIES) {
            el.scrollTop = saved
            restored = true
          }
        } else {
          restored = true
        }
      }
      if (!restored && tries < MAX_TRIES) {
        tries++
        raf = requestAnimationFrame(tick)
      }
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      // Финальное сохранение перед уходом со страницы.
      if (el) sessionStorage.setItem(storageKey, String(el.scrollTop))
      detach?.()
    }
  }, [storageKey, ready])
}

/**
 * Восстановление скролла главного контейнера `<main>` по pathname. Возвращает
 * ref, который нужно повесить на скроллящий элемент. Вешается один раз в Layout
 * — покрывает все не-grid страницы (карточки, формы) автоматически.
 */
export function useMainScrollRestoration() {
  const { pathname } = useLocation()
  const ref = useRef<HTMLElement>(null)
  useScrollRestoration(pathname, () => ref.current)
  return ref
}

// Сколько кадров подряд «долбим» сохранённую позицию после готовности контента.
// AG Grid в окне инициализации (возврат с данными из кэша) сбрасывает scrollTop
// почти каждый кадр — поэтому не ждём «прилипания», а переустанавливаем позицию
// фиксированное окно (~0.4с при 60fps), гарантированно перекрывая инициализацию.
const SETTLE_FRAMES = 24

/**
 * Восстановление позиции скролла списка на AG Grid при навигации «список →
 * деталь → назад». Сохраняет точный `scrollTop` вьюпорта `.ag-body-viewport`
 * и восстанавливает его «долбёжкой»: после загрузки данных переустанавливает
 * позицию каждый кадр в течение SETTLE_FRAMES, перекрывая сброс от AG Grid.
 * Индекс строки (getFirstDisplayedRowIndex) не годится — он включает буфер
 * виртуализации (~10 строк выше видимой), позиция уезжала вверх.
 *
 * @param key          уникальный ключ списка (pathname [+ суффикс])
 * @param containerRef контейнер с `.ag-body-viewport`
 */
export function useAgGridScrollRestoration(
  key: string,
  containerRef: RefObject<HTMLElement | null>,
) {
  const storageKey = KEY_PREFIX + key
  useEffect(() => {
    const saved = Number(sessionStorage.getItem(storageKey) ?? '0')
    let viewport: HTMLElement | null = null
    let detach: (() => void) | null = null
    let raf = 0
    let tries = 0
    let settle = 0
    // Фаза восстановления: пока true — программно держим позицию и НЕ
    // перезаписываем сохранённое значение событиями скролла (они летят от
    // нашего же scrollTop). Снимается, когда восстановление завершено.
    let restoring = saved > 0

    let attached = false

    const onScroll = () => {
      // Сохраняем позицию при пользовательском скролле. Во время фазы
      // восстановления (restoring) не пишем — событие летит от нашего же
      // программного scrollTop.
      if (viewport && !restoring) sessionStorage.setItem(storageKey, String(viewport.scrollTop))
    }

    const tick = () => {
      const vp = containerRef.current?.querySelector<HTMLElement>('.ag-body-viewport')
      if (vp && vp !== viewport) {
        detach?.()
        viewport = vp
        attached = true
        vp.addEventListener('scroll', onScroll, { passive: true })
        detach = () => vp.removeEventListener('scroll', onScroll)
      }
      if (restoring && viewport) {
        const maxScroll = viewport.scrollHeight - viewport.clientHeight
        // Долбим позицию только когда контента уже хватает (данные загружены).
        if (maxScroll >= saved - 4) {
          viewport.scrollTop = saved
          settle++
          if (settle >= SETTLE_FRAMES) restoring = false
        }
      }
      // Продолжаем цикл, пока идёт восстановление ИЛИ пока ещё не привязали
      // слушатель скролла (вьюпорт AG Grid появляется не в первом кадре —
      // без этого на первом заходе позиция вообще не сохранялась).
      if ((restoring || !attached) && tries < MAX_TRIES) {
        tries++
        raf = requestAnimationFrame(tick)
      } else {
        restoring = false
      }
    }
    raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(raf)
      // НЕ сохраняем в cleanup: при unmount AG Grid уже сбросил scrollTop в 0,
      // и мы бы затёрли актуальное значение. Последняя позиция уже записана
      // обработчиком onScroll при пользовательском скролле.
      detach?.()
    }
  }, [storageKey, containerRef])
}
